import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Popover } from '@mui/material';
import { SButton, SIcon, SPopover, STooltip } from '@chargebee/sting-react';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import { TriggerEventType } from '@bbkAdminComponents/play/play-types';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { type Play } from '@bbkAdminRedux/rtkq/play.slice';
import {
  ReportType,
  useExportCustomersData,
  type ReportFilter,
  type ReportFilters,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { type LCOReportOpts } from '@bbkAdminUtils/api-client/reports-api';
import { cs } from '@bbkAdminUtils/css-utils';
import { Icons } from '@bbkAdminUtils/icons';
import { useGetProductLicense } from '@bbkAdminUtils/plans';

const getTargetType = (play: Play): 'HOSTED_PAGE' | 'OFFER' =>
  play.trigger_rules?.event_type === TriggerEventType.ON_CLICK
    ? 'HOSTED_PAGE'
    : 'OFFER';

const usePlayExport = (
  buildFilters: () => ReportFilters,
  opts?: { orFilters?: boolean }
) => {
  const appKey = useSelector(selectAppId);
  const companyId = useSelector(selectCompanyInternalName);
  const showNewPlayReportsUI = useGetProductLicense().agsLicense;
  const { startDate: start, endDate: end } = useSelectDatePicker();
  const exportLCOReportingData = useExportCustomersData();

  const [openExportMenu, setOpenExportMenu] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const exportMenuAnchorRef = useRef<any>(null);

  const exportData = (reportType: ReportType) => {
    setOpenExportMenu(false);
    const lcoReportOpts: LCOReportOpts = {
      start,
      end,
      format: 'CSV',
      reportType,
      filters: buildFilters(),
      ...(opts?.orFilters && { orFilters: true }),
    };

    return exportLCOReportingData(lcoReportOpts, {
      appId: appKey,
      companyId,
      returnDownloadUrl: false,
    }).catch((e: Error) => {
      console.error(e.message);
    });
  };

  return {
    showNewPlayReportsUI,
    openExportMenu,
    setOpenExportMenu,
    exportMenuAnchorRef,
    exportData,
  };
};

const buildPlayFilters = (plays: Play[]): ReportFilters =>
  plays.map((play) => [
    { field: 'play_id', operator: 'equals' as const, value: play.id },
    {
      field: 'target_type',
      operator: 'equals' as const,
      value: getTargetType(play),
    },
  ]);

// ---------------------------------------------------------------------------
// V1 (MUI Popover)
// ---------------------------------------------------------------------------

const ExportPlayDataUI: React.FC<{
  isDemoData?: boolean;
  variant?: 'primary' | 'neutral';
  openExportMenu: boolean;
  setOpenExportMenu: (open: boolean) => void;
  exportMenuAnchorRef: React.MutableRefObject<any>;
  showNewPlayReportsUI: boolean | undefined;
  exportData: (reportType: ReportType) => void;
}> = ({
  isDemoData,
  variant = 'primary',
  openExportMenu,
  setOpenExportMenu,
  exportMenuAnchorRef,
  showNewPlayReportsUI,
  exportData,
}) => {
  const isNeutral = variant === 'neutral';
  const textColor = isNeutral
    ? 'tw-text-cbrneutral-700'
    : 'tw-text-cbrprimary-700';
  const textColorDimmed = isNeutral
    ? 'tw-text-cbrneutral-700/40'
    : 'tw-text-cbrprimary-700/40';

  const exportButton = (
    <button
      className={cs(`export-button tw-py-1 tw-font-semibold ${textColor}`, {
        [textColorDimmed]: isDemoData,
      })}
      onClick={() => setOpenExportMenu(true)}
      ref={exportMenuAnchorRef}
      disabled={isDemoData}
    >
      <span className="export-icon tw-px-2">{Icons.export}</span>
      <span>Export CSV</span>
      <span
        className={cs('tw-px-2', {
          'hide-icon': openExportMenu,
        })}
      >
        {Icons.cbrChevronDown}
      </span>
    </button>
  );

  const exportMenu = (
    <Popover
      id="dropdown"
      open={openExportMenu}
      anchorEl={exportMenuAnchorRef?.current}
      onClose={() => setOpenExportMenu(false)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <div className="tw-flex tw-max-w-60 tw-flex-col">
        <button
          onClick={() => {
            exportData(
              showNewPlayReportsUI
                ? ReportType.lifecycle_entity_metrics_customer_snapshot_export
                : ReportType.lifecycle_customer_snapshot
            );
          }}
          className="tw-p-4 tw-text-start hover:tw-bg-cbrprimary-50"
        >
          <div className="tw-font-semibold">Customer level report</div>
          <div className="tw-text-xs">
            List of all the customers who engaged with the offers in the play.
          </div>
        </button>
        <button
          onClick={() => {
            exportData(
              showNewPlayReportsUI
                ? ReportType.lifecycle_entity_metrics_user_export
                : ReportType.lifecycle_customer_snapshot_export_user
            );
          }}
          className="tw-p-4 tw-text-start hover:tw-bg-cbrprimary-50"
        >
          <div className="tw-font-semibold">User level report</div>
          <div className="tw-text-xs">
            List of all the users who engaged with offers along with their
            status.
          </div>
        </button>
      </div>
    </Popover>
  );

  return (
    <>
      {isDemoData ? (
        <TippyTooltip content={<>Disabled in demo mode</>}>
          <div>{exportButton}</div>
        </TippyTooltip>
      ) : (
        exportButton
      )}
      {exportMenu}
    </>
  );
};

export const ExportPlayData: React.FC<{
  playId: string;
  offerId: string | null | undefined;
  targetType: 'HOSTED_PAGE' | 'OFFER';
}> = ({ playId, offerId, targetType }) => {
  const isDemoData = useShowPlayDemoData();
  const offerSelected = offerId !== null && offerId !== undefined;

  const hook = usePlayExport(() => [
    [
      { field: 'play_id', operator: 'equals', value: playId },
      ...(offerSelected
        ? ([
            { field: 'offer_id', operator: 'equals', value: offerId },
          ] as ReportFilter[])
        : []),
      { field: 'target_type', operator: 'equals', value: targetType },
    ],
  ]);

  return <ExportPlayDataUI isDemoData={isDemoData} {...hook} />;
};

export const BulkExportPlayData: React.FC<{ plays: Play[] }> = ({ plays }) => {
  const hook = usePlayExport(() => buildPlayFilters(plays), {
    orFilters: plays.length > 1,
  });
  return <ExportPlayDataUI variant="neutral" {...hook} />;
};

// ---------------------------------------------------------------------------
// V2 (Sting design system)
// ---------------------------------------------------------------------------

const ExportPlayDataUIV2: React.FC<{
  isDemoData?: boolean;
  disabled?: boolean;
  variant?:
    | 'primary'
    | 'neutral'
    | 'neutral-outline'
    | 'primary-ghost'
    | 'primary-outline'
    | 'neutral-ghost'
    | 'danger'
    | 'danger-outline'
    | 'danger-ghost'
    | undefined;
  openExportMenu: boolean;
  setOpenExportMenu: (open: boolean) => void;
  exportData: (reportType: ReportType) => void;
}> = ({
  isDemoData,
  disabled,
  variant = 'primary-ghost',
  openExportMenu,
  setOpenExportMenu,
  exportData,
}) => {
  const exportButton = (
    <SPopover open={openExportMenu} onOpenChange={setOpenExportMenu}>
      <SPopover.Trigger asChild>
        <SButton
          variant={variant}
          icon={<SIcon name="download" size={16} />}
          iconPosition="left"
          disabled={disabled || isDemoData}
        >
          Export CSV
          <SIcon name="chevron-down" size={14} />
        </SButton>
      </SPopover.Trigger>
      <SPopover.Content align="end" className="!tw-overflow-hidden !tw-p-0">
        <div className="tw-flex tw-max-w-80 tw-flex-col">
          <button
            onClick={() =>
              exportData(
                ReportType.lifecycle_entity_metrics_customer_snapshot_export
              )
            }
            className="tw-px-4 tw-py-2 tw-text-start hover:tw-bg-primary-50"
          >
            <div className="tw-font-semibold">Customer level report</div>
            <div className="tw-text-xs">
              List of all the customers who engaged with the offers in the play.
            </div>
          </button>
          <button
            onClick={() =>
              exportData(ReportType.lifecycle_entity_metrics_user_export)
            }
            className="tw-px-4 tw-py-2 tw-text-start hover:tw-bg-primary-50"
          >
            <div className="tw-font-semibold">User level report</div>
            <div className="tw-text-xs">
              List of all the users who engaged with offers along with their
              status.
            </div>
          </button>
        </div>
      </SPopover.Content>
    </SPopover>
  );

  return isDemoData ? (
    <STooltip label="Disabled in demo mode." width="regular" placement="left">
      <div>{exportButton}</div>
    </STooltip>
  ) : (
    exportButton
  );
};

export const ExportPlayDataV2: React.FC<{
  playId: string;
  offerId: string | null | undefined;
  disabled?: boolean;
}> = ({ playId, offerId, disabled }) => {
  const isDemoData = useShowPlayDemoData();
  const offerSelected = offerId !== null && offerId !== undefined;

  const hook = usePlayExport(() => [
    [
      { field: 'play_id', operator: 'equals', value: playId },
      ...(offerSelected
        ? ([
            { field: 'offer_id', operator: 'equals', value: offerId },
          ] as ReportFilter[])
        : []),
      { field: 'target_type', operator: 'equals', value: 'OFFER' },
    ],
  ]);

  return (
    <ExportPlayDataUIV2 isDemoData={isDemoData} disabled={disabled} {...hook} />
  );
};

export const BulkExportPlayDataV2: React.FC<{ plays: Play[] }> = ({
  plays,
}) => {
  const hook = usePlayExport(() => buildPlayFilters(plays), {
    orFilters: plays.length > 1,
  });
  return <ExportPlayDataUIV2 variant="neutral-outline" {...hook} />;
};
